import { ArrowLeft, ArrowRight, GitBranch } from 'lucide-react';
import type * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { Callout } from '@/components/patterns/callout';
import { Stat, StatRow } from '@/components/patterns/metric';
import { EstadoErroOuIndisponivel, EstadoVazio } from '@/components/patterns/states';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { ehAcessoNegado } from '@/data/http';
import type { Ramo } from '@/data/types';
import { useResource } from '@/hooks/use-resource';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { FeedPage } from '@/features/feed/FeedPage';

/**
 * Ramos — as versões agrupadas por sprint (o 2º segmento da versão: 2602,
 * 2701…). Cada card leva ao mesmo feed, já filtrado pelo ramo.
 */
export function RamosPage() {
  const ramos = useResource((signal) => dataSource.listarRamos(undefined, signal), []);

  return (
    <PageBody>
      <PageHeader
        eyebrow="Compilações"
        titulo="Ramos"
        descricao="Um ramo é a sprint da versão (o 2º número: 2.2602.78.0 é do ramo 2602). Cada ramo reúne o branch de release (v2.<ramo>) e os builds noturnos de master."
      />

      {ramos.estado === 'carregando' ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" role="status" aria-label="Carregando ramos">
          {Array.from({ length: 6 }).map((_, indice) => (
            <Skeleton key={indice} className="h-56 rounded-card" />
          ))}
        </div>
      ) : ramos.estado === 'erro' ? (
        <Card>
          <EstadoErroOuIndisponivel erro={ramos.erro} aoTentarDeNovo={ramos.recarregar} />
        </Card>
      ) : !ramos.dados || ramos.dados.length === 0 ? (
        <Card>
          <EstadoVazio titulo="Nenhum ramo ainda" descricao="Os ramos aparecem assim que o Jenkins publica a primeira versão." />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {ramos.dados.map((ramo) => (
            <li key={ramo.ramo}>
              <RamoCard ramo={ramo} />
            </li>
          ))}
        </ul>
      )}
    </PageBody>
  );
}

function Numero({ rotulo, valor, tom }: { rotulo: string; valor: number; tom?: string }) {
  return (
    <div className="flex flex-col">
      <dt className="cmp-eyebrow">{rotulo}</dt>
      <dd className={`font-narrow text-section-title tabular-nums ${tom ?? ''}`}>{valor.toLocaleString('pt-BR')}</dd>
    </div>
  );
}

function RamoCard({ ramo }: { ramo: Ramo }) {
  return (
    <Link
      to={`/ramos/${encodeURIComponent(ramo.ramo)}`}
      className="group flex h-full flex-col gap-4 rounded-card border border-border bg-bg-surface p-5 shadow-sm transition-colors duration-fast ease-standard hover:border-primary-border hover:bg-primary-wash"
      aria-label={`Ramo ${ramo.ramo}: ${plural(ramo.total, 'versão', 'versões')}, ${ramo.nao_homologadas} não homologadas`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="cmp-eyebrow">Ramo</span>
          <span className="font-mono text-page-title tabular-nums">{ramo.ramo}</span>
        </div>
        <ArrowRight className="mt-2 size-5 text-icon transition-transform duration-fast ease-standard group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ramo.branches.map((branch) => (
          <Badge key={branch} tone={branch === 'master' ? 'neutral' : 'primary'} icon={<GitBranch aria-hidden="true" />} className="font-mono">
            {branch}
          </Badge>
        ))}
      </div>

      <dl className="grid grid-cols-4 gap-2">
        <Numero rotulo="Total" valor={ramo.total} />
        <Numero rotulo="Não homol." valor={ramo.nao_homologadas} tom={ramo.nao_homologadas ? 'text-on-surface' : 'text-on-surface-muted'} />
        <Numero rotulo="Homolog." valor={ramo.homologados} tom="text-success" />
        <Numero rotulo="Reprov." valor={ramo.reprovados} tom={ramo.reprovados ? 'text-danger' : 'text-on-surface-muted'} />
      </dl>

      <dl className="mt-auto grid gap-2 border-t border-border-subtle pt-3 text-cell">
        <div className="flex flex-wrap justify-between gap-x-3">
          <dt className="text-on-surface-muted">Última versão</dt>
          <dd className="font-mono">
            {ramo.ultima_versao ?? 'Sem dado'}
            {ramo.ultima_publicacao ? (
              <span className="ml-1.5 font-sans text-on-surface-muted" title={formatarDataHora(ramo.ultima_publicacao)}>
                · {formatarRelativo(ramo.ultima_publicacao)}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex flex-wrap justify-between gap-x-3">
          <dt className="text-on-surface-muted">Última homologada</dt>
          <dd className="font-mono">{ramo.ultima_homologada ?? <span className="font-sans text-on-surface-muted">Nenhuma ainda</span>}</dd>
        </div>
      </dl>
    </Link>
  );
}

/** `/ramos/:ramo` — o feed filtrado pelo ramo, com o cabeçalho e os números dele. */
export function RamoFeedPage() {
  const { ramo = '' } = useParams();
  const ramos = useResource((signal) => dataSource.listarRamos(undefined, signal), []);
  const dados = ramos.dados?.find((item) => item.ramo === ramo) ?? null;

  const cabecalho = (seletorVista: React.ReactNode) => (
    <div className="flex flex-col gap-4">
      <Link to="/ramos" className="inline-flex w-fit items-center gap-1.5 text-label text-primary underline-offset-4 hover:underline">
        <ArrowLeft className="size-3.5" aria-hidden="true" />
        Todos os ramos
      </Link>
      <PageHeader
        eyebrow="Ramo"
        acao={seletorVista}
        titulo={`Ramo ${ramo}`}
        descricao={
          dados
            ? `Branches ${dados.branches.join(', ')}. Última versão ${dados.ultima_versao ?? '—'}${dados.ultima_homologada ? `; última homologada ${dados.ultima_homologada}` : '; nenhuma homologada ainda'}.`
            : 'Versões publicadas deste ramo, da mais recente para a mais antiga.'
        }
      />
      {ramos.estado === 'erro' && ehAcessoNegado(ramos.erro) ? null : ramos.estado === 'erro' ? (
        <Callout tom="erro">Não foi possível carregar os números do ramo: {ramos.erro?.message}</Callout>
      ) : dados ? (
        <Card as="section" aria-label={`Números do ramo ${ramo}`}>
          <StatRow>
            <Stat rotulo="Versões" valor={dados.total.toLocaleString('pt-BR')} />
            <Stat rotulo="Não homologadas" valor={dados.nao_homologadas.toLocaleString('pt-BR')} />
            <Stat rotulo="Homologadas" valor={dados.homologados.toLocaleString('pt-BR')} />
            <Stat rotulo="Reprovadas" valor={dados.reprovados.toLocaleString('pt-BR')} />
          </StatRow>
        </Card>
      ) : ramos.estado === 'carregando' ? (
        <Skeleton className="h-[104px] rounded-card" />
      ) : null}
    </div>
  );

  return <FeedPage key={ramo} ramoFixo={ramo} cabecalho={cabecalho} />;
}
